import { useState, useMemo } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Divider,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material'
import CalculateIcon from '@mui/icons-material/Calculate'
import BusinessIcon from '@mui/icons-material/Business'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import { TREATMENT_COST_PER_TON } from '../services/degradationCurve'

const KWH_TO_CO2_KG = 0.42

// avgCost calculated from avgCapacity * TREATMENT_COST_PER_TON
const equipmentDefaults = {
  Chiller: { improvement: 21.1, avgCapacity: 200, get avgCost() { return this.avgCapacity * TREATMENT_COST_PER_TON } },
  RTU: { improvement: 22.3, avgCapacity: 15, get avgCost() { return this.avgCapacity * TREATMENT_COST_PER_TON } },
  CRAC: { improvement: 26.1, avgCapacity: 20, get avgCost() { return this.avgCapacity * TREATMENT_COST_PER_TON } },
  'Split System': { improvement: 24.3, avgCapacity: 5, get avgCost() { return this.avgCapacity * TREATMENT_COST_PER_TON } }
}

const operatingHours = {
  '24/7': 8760,
  'Extended': 4000,
  'Commercial': 2500,
  'Seasonal': 1500
}

function FleetImpact() {
  const [fleet, setFleet] = useState({
    chillers: { count: 2, avgCapacity: 200 },
    rtus: { count: 10, avgCapacity: 15 },
    cracs: { count: 0, avgCapacity: 20 },
    splits: { count: 5, avgCapacity: 5 }
  })
  const [avgAge, setAvgAge] = useState(12)
  const [profile, setProfile] = useState('Commercial')
  const [utilityRate, setUtilityRate] = useState(0.12)
  const [paymentOption, setPaymentOption] = useState('upfront') // 'upfront' or 'deferred'

  const results = useMemo(() => {
    const hours = operatingHours[profile]
    const ageModifier = avgAge < 5 ? 0.85 : avgAge < 10 ? 1.0 : avgAge < 15 ? 1.05 : 1.0

    let totalTons = 0
    let totalKwhSaved = 0
    let breakdown = []

    // Chillers
    if (fleet.chillers.count > 0) {
      const tons = fleet.chillers.count * fleet.chillers.avgCapacity
      const improvement = equipmentDefaults.Chiller.improvement * ageModifier / 100
      const kwhSaved = tons * 1.3 * hours * improvement
      totalTons += tons
      totalKwhSaved += kwhSaved
      breakdown.push({ type: 'Chillers', count: fleet.chillers.count, tons, kwhSaved })
    }

    // RTUs
    if (fleet.rtus.count > 0) {
      const tons = fleet.rtus.count * fleet.rtus.avgCapacity
      const improvement = equipmentDefaults.RTU.improvement * ageModifier / 100
      const kwhSaved = tons * 1.4 * hours * improvement
      totalTons += tons
      totalKwhSaved += kwhSaved
      breakdown.push({ type: 'RTUs', count: fleet.rtus.count, tons, kwhSaved })
    }

    // CRACs
    if (fleet.cracs.count > 0) {
      const tons = fleet.cracs.count * fleet.cracs.avgCapacity
      const improvement = equipmentDefaults.CRAC.improvement * ageModifier / 100
      const kwhSaved = tons * 1.5 * hours * improvement
      totalTons += tons
      totalKwhSaved += kwhSaved
      breakdown.push({ type: 'CRACs', count: fleet.cracs.count, tons, kwhSaved })
    }

    // Splits
    if (fleet.splits.count > 0) {
      const tons = fleet.splits.count * fleet.splits.avgCapacity
      const improvement = equipmentDefaults['Split System'].improvement * ageModifier / 100
      const kwhSaved = tons * 1.4 * hours * improvement
      totalTons += tons
      totalKwhSaved += kwhSaved
      breakdown.push({ type: 'Split Systems', count: fleet.splits.count, tons, kwhSaved })
    }

    const treatmentCost = totalTons * TREATMENT_COST_PER_TON
    const annualSavings = totalKwhSaved * utilityRate
    const paybackMonths = annualSavings > 0 ? (treatmentCost / annualSavings) * 12 : 0
    const co2Avoided = totalKwhSaved * KWH_TO_CO2_KG / 1000
    const vehicleEquivalent = Math.round(co2Avoided / 4.6) // avg car = 4.6 tonnes/year

    // Calculate payment-specific cash flow metrics
    const annualPayment = paymentOption === 'deferred'
      ? treatmentCost / 3
      : treatmentCost
    const year1NetCashFlow = paymentOption === 'deferred'
      ? annualSavings - (treatmentCost / 3)
      : annualSavings - treatmentCost
    const cashFlowPositiveYear1 = paymentOption === 'deferred'
      && annualSavings > (treatmentCost / 3)

    // Monthly payment breakdown (36 monthly payments over 3 years)
    const monthlyPayment = paymentOption === 'deferred'
      ? treatmentCost / 36
      : null
    const monthlySavings = annualSavings / 12
    const monthlyNetCashFlow = paymentOption === 'deferred'
      ? monthlySavings - monthlyPayment
      : null
    const cashFlowPositiveMonthly = monthlyNetCashFlow !== null && monthlyNetCashFlow > 0

    return {
      totalUnits: fleet.chillers.count + fleet.rtus.count + fleet.cracs.count + fleet.splits.count,
      totalTons,
      treatmentCost,
      annualKwhSaved: Math.round(totalKwhSaved),
      annualSavings: Math.round(annualSavings),
      paybackMonths: paybackMonths.toFixed(1),
      fiveYearSavings: Math.round(annualSavings * 5),
      fiveYearROI: annualSavings > 0 ? Math.round(((annualSavings * 5) - treatmentCost) / treatmentCost * 100) : 0,
      co2Avoided: co2Avoided.toFixed(1),
      vehicleEquivalent,
      breakdown,
      // Payment option metrics
      annualPayment: Math.round(annualPayment),
      year1NetCashFlow: Math.round(year1NetCashFlow),
      cashFlowPositiveYear1,
      // Monthly breakdown (for 3-year deferred)
      monthlyPayment: monthlyPayment ? Math.round(monthlyPayment) : null,
      monthlySavings: Math.round(monthlySavings),
      monthlyNetCashFlow: monthlyNetCashFlow !== null ? Math.round(monthlyNetCashFlow) : null,
      cashFlowPositiveMonthly
    }
  }, [fleet, avgAge, profile, utilityRate, paymentOption])

  const handleFleetChange = (type, field) => (e) => {
    setFleet(prev => ({
      ...prev,
      [type]: { ...prev[type], [field]: Number(e.target.value) || 0 }
    }))
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 4 }}>
        Fleet Impact Calculator
      </Typography>

      <Grid container spacing={4}>
        {/* Input Form */}
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <BusinessIcon color="primary" />
                <Typography variant="h6">Portfolio Details</Typography>
              </Box>

              {/* Chillers */}
              <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5, fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Chillers
              </Typography>
              <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Count"
                    type="number"
                    value={fleet.chillers.count}
                    onChange={handleFleetChange('chillers', 'count')}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Avg Tons"
                    type="number"
                    value={fleet.chillers.avgCapacity}
                    onChange={handleFleetChange('chillers', 'avgCapacity')}
                  />
                </Grid>
              </Grid>

              {/* RTUs */}
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Rooftop Units
              </Typography>
              <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Count"
                    type="number"
                    value={fleet.rtus.count}
                    onChange={handleFleetChange('rtus', 'count')}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Avg Tons"
                    type="number"
                    value={fleet.rtus.avgCapacity}
                    onChange={handleFleetChange('rtus', 'avgCapacity')}
                  />
                </Grid>
              </Grid>

              {/* CRACs */}
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                CRAC Units
              </Typography>
              <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Count"
                    type="number"
                    value={fleet.cracs.count}
                    onChange={handleFleetChange('cracs', 'count')}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Avg Tons"
                    type="number"
                    value={fleet.cracs.avgCapacity}
                    onChange={handleFleetChange('cracs', 'avgCapacity')}
                  />
                </Grid>
              </Grid>

              {/* Splits */}
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Split Systems
              </Typography>
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Count"
                    type="number"
                    value={fleet.splits.count}
                    onChange={handleFleetChange('splits', 'count')}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Avg Tons"
                    type="number"
                    value={fleet.splits.avgCapacity}
                    onChange={handleFleetChange('splits', 'avgCapacity')}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <TextField
                fullWidth
                label="Average Equipment Age (years)"
                type="number"
                value={avgAge}
                onChange={(e) => setAvgAge(Number(e.target.value))}
                sx={{ mb: 2 }}
              />

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Operating Profile</InputLabel>
                <Select
                  value={profile}
                  label="Operating Profile"
                  onChange={(e) => setProfile(e.target.value)}
                >
                  <MenuItem value="24/7">24/7</MenuItem>
                  <MenuItem value="Extended">Extended Hours</MenuItem>
                  <MenuItem value="Commercial">Commercial</MenuItem>
                  <MenuItem value="Seasonal">Seasonal</MenuItem>
                </Select>
              </FormControl>

              {/* Payment Option */}
              <Box sx={{ mb: 2 }}>
                <Typography gutterBottom variant="body2" color="text.secondary">
                  Payment Option
                </Typography>
                <ToggleButtonGroup
                  value={paymentOption}
                  exclusive
                  onChange={(_, value) => value && setPaymentOption(value)}
                  fullWidth
                  size="small"
                >
                  <ToggleButton value="upfront">
                    Upfront Payment
                  </ToggleButton>
                  <ToggleButton value="deferred">
                    3-Year Deferred
                  </ToggleButton>
                </ToggleButtonGroup>
                {paymentOption === 'deferred' && (
                  <Box sx={{ mt: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.primary" sx={{ display: 'block', fontWeight: 600 }}>
                      36 Monthly Payments
                    </Typography>
                    <Typography variant="body2" color="success.main" sx={{ fontWeight: 700 }}>
                      ${results.monthlyPayment?.toLocaleString()}/month
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Est. monthly savings: ${results.monthlySavings?.toLocaleString()} → Net:{' '}
                      <span style={{ color: results.cashFlowPositiveMonthly ? '#2e7d32' : 'inherit', fontWeight: 600 }}>
                        {results.monthlyNetCashFlow >= 0 ? '+' : ''}${results.monthlyNetCashFlow?.toLocaleString()}/month
                      </span>
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                      Subject to credit approval. Other financing options may be available.
                    </Typography>
                  </Box>
                )}
              </Box>

              <TextField
                fullWidth
                label="Utility Rate ($/kWh)"
                type="number"
                inputProps={{ step: 0.01 }}
                value={utilityRate}
                onChange={(e) => setUtilityRate(Number(e.target.value))}
                sx={{ mb: 3 }}
              />

              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<CalculateIcon />}
              >
                Calculate Portfolio Impact
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Results */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" gutterBottom>
                Portfolio Impact Summary
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="primary.main" fontWeight={700}>
                      {results.totalUnits}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total Units
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="primary.main" fontWeight={700}>
                      {results.totalTons.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total Tons
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="warning.main" fontWeight={700}>
                      ${results.treatmentCost.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Treatment Cost
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main" fontWeight={700}>
                      {results.paybackMonths}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Payback (months)
                    </Typography>
                  </Paper>
                </Grid>

                {/* Year 1 Net Cash Flow */}
                <Grid item xs={12}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      bgcolor: (theme) => results.cashFlowPositiveYear1
                        ? (theme.palette.mode === 'light' ? 'success.50' : 'rgba(46, 125, 50, 0.15)')
                        : 'inherit',
                      borderColor: results.cashFlowPositiveYear1 ? 'success.main' : 'divider',
                      borderWidth: results.cashFlowPositiveYear1 ? 2 : 1
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                      <AccountBalanceIcon color={results.year1NetCashFlow >= 0 ? 'success' : 'warning'} />
                      <Typography variant="body2" color="text.secondary">
                        {paymentOption === 'deferred' ? 'Cash Flow Summary' : 'Year 1 Net Cash Flow'}
                      </Typography>
                      {results.cashFlowPositiveYear1 && (
                        <Chip label="Cash Flow Positive" size="small" color="success" />
                      )}
                      <Chip
                        label={paymentOption === 'deferred' ? '36 Monthly Payments' : 'Upfront'}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                    {paymentOption === 'deferred' ? (
                      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Month 1 Net
                          </Typography>
                          <Typography
                            variant="h5"
                            color={results.monthlyNetCashFlow >= 0 ? 'success.main' : 'warning.main'}
                            fontWeight={700}
                          >
                            {results.monthlyNetCashFlow >= 0 ? '+' : ''}${results.monthlyNetCashFlow?.toLocaleString()}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Year 1 Total
                          </Typography>
                          <Typography
                            variant="h5"
                            color={results.year1NetCashFlow >= 0 ? 'success.main' : 'warning.main'}
                            fontWeight={700}
                          >
                            {results.year1NetCashFlow >= 0 ? '+' : ''}${results.year1NetCashFlow.toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Typography
                        variant="h4"
                        color={results.year1NetCashFlow >= 0 ? 'success.main' : 'warning.main'}
                        fontWeight={700}
                      >
                        ${results.year1NetCashFlow.toLocaleString()}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {paymentOption === 'deferred'
                        ? `Monthly: $${results.monthlySavings?.toLocaleString()} savings − $${results.monthlyPayment?.toLocaleString()} payment`
                        : `Savings $${results.annualSavings.toLocaleString()} − Treatment $${results.treatmentCost.toLocaleString()}`
                      }
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Annual Energy Savings
                  </Typography>
                  <Typography variant="h5" fontWeight={600}>
                    {results.annualKwhSaved.toLocaleString()} kWh
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Annual Cost Savings
                  </Typography>
                  <Typography variant="h5" fontWeight={600} color="success.main">
                    ${results.annualSavings.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    5-Year Savings
                  </Typography>
                  <Typography variant="h5" fontWeight={600}>
                    ${results.fiveYearSavings.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    5-Year ROI
                  </Typography>
                  <Typography variant="h5" fontWeight={600} color="primary.main">
                    {results.fiveYearROI}%
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Environmental Impact
              </Typography>
              <Box sx={{ display: 'flex', gap: 4 }}>
                <Box>
                  <Typography variant="h6">
                    {results.co2Avoided} tonnes
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    CO2 avoided annually
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h6">
                    {results.vehicleEquivalent} vehicles
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Equivalent removed from road
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* 5-Year Cash Flow Projection */}
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                5-Year Cash Flow Projection
                <Chip
                  label={paymentOption === 'deferred' ? '36 Monthly Payments' : 'Upfront'}
                  size="small"
                  variant="outlined"
                />
              </Typography>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Year</TableCell>
                    <TableCell align="right">Savings</TableCell>
                    <TableCell align="right">Payment</TableCell>
                    <TableCell align="right">Net Cash Flow</TableCell>
                    <TableCell align="right">Cumulative</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[1, 2, 3, 4, 5].map((year) => {
                    const yearPayment = paymentOption === 'deferred' && year <= 3
                      ? results.treatmentCost / 3
                      : (year === 1 && paymentOption === 'upfront' ? results.treatmentCost : 0)
                    const netCashFlow = results.annualSavings - yearPayment
                    const cumulativePayment = paymentOption === 'deferred'
                      ? Math.min(year, 3) * (results.treatmentCost / 3)
                      : results.treatmentCost
                    const cumulativeNet = (results.annualSavings * year) - cumulativePayment

                    return (
                      <TableRow key={year}>
                        <TableCell>Year {year}</TableCell>
                        <TableCell align="right">${results.annualSavings.toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ color: 'text.secondary' }}>
                          {yearPayment > 0 ? `-$${Math.round(yearPayment).toLocaleString()}` : '—'}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ color: netCashFlow >= 0 ? 'success.main' : 'error.main', fontWeight: 600 }}
                        >
                          ${Math.round(netCashFlow).toLocaleString()}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ color: cumulativeNet >= 0 ? 'success.main' : 'error.main' }}
                        >
                          ${Math.round(cumulativeNet).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}

export default FleetImpact
